/**
 * Log Formatters Utility
 * Provides formatting functions for displaying audit logs in a user-friendly way
 */

import { formatValueForDisplay } from './changeDetector';

/**
 * Format a log entry for display in the UI
 * @param {object} logEntry - Raw log entry from Firestore
 * @returns {object} - Formatted log entry
 */
export const formatLogEntry = (logEntry) => {
  return {
    id: logEntry.id,
    timestamp: formatTimestamp(logEntry.timestamp),
    user: formatUser(logEntry.userEmail, logEntry.userId),
    action: formatAction(logEntry.action),
    resource: formatResource(logEntry.resource),
    resourceId: logEntry.resourceId,
    summary: generateLogSummary(logEntry),
    details: formatLogDetails(logEntry),
    severity: logEntry.severity,
    severityColor: getSeverityColor(logEntry.severity),
    severityIcon: getSeverityIcon(logEntry.severity),
    tags: logEntry.tags || [],
    metadata: logEntry.metadata || {}
  };
};

/**
 * Format timestamp for display
 * @param {object} timestamp - Firestore timestamp
 * @returns {object} - Formatted timestamp info
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return { display: 'Unknown', relative: 'Unknown', full: 'Unknown' };
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  let relative;
  if (diffMins < 1) relative = 'Just now';
  else if (diffMins < 60) relative = `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  else if (diffHours < 24) relative = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  else if (diffDays < 7) relative = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  else relative = date.toLocaleDateString();
  
  return {
    display: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    relative,
    full: date.toLocaleString(),
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString()
  };
};

/**
 * Format user information for display
 * @param {string} userEmail - User email
 * @param {string} userId - User ID
 * @returns {object} - Formatted user info
 */
export const formatUser = (userEmail, userId) => {
  // Handle various email formats and fallbacks
  let email = userEmail;
  
  if (!email || email === 'unknown@unknown.com' || email === 'current.user@example.com') {
    email = 'Unknown User';
  }
  
  // Extract name from email if possible
  let name;
  if (email.includes('@') && !email.startsWith('unknown@') && !email.startsWith('current.user@')) {
    name = email.split('@')[0].replace(/[._]/g, ' ');
    name = name.charAt(0).toUpperCase() + name.slice(1);
  } else {
    name = email === 'Unknown User' ? 'Unknown User' : email;
  }
  
  // Generate initials
  const initials = name.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'UN';
  
  return {
    email: email,
    name: name,
    initials: initials,
    id: userId
  };
};

/**
 * Format action for display
 * @param {string} action - Raw action string
 * @returns {object} - Formatted action info
 */
export const formatAction = (action) => {
  const actionMap = {
    CREATE: { label: 'Created', icon: '➕', color: 'text-green-600' },
    UPDATE: { label: 'Updated', icon: '✏️', color: 'text-blue-600' },
    DELETE: { label: 'Deleted', icon: '🗑️', color: 'text-red-600' },
    VIEW: { label: 'Viewed', icon: '👁️', color: 'text-gray-600' },
    BULK_CREATE: { label: 'Bulk Created', icon: '📦', color: 'text-green-600' },
    BULK_UPDATE: { label: 'Bulk Updated', icon: '📝', color: 'text-blue-600' },
    BULK_DELETE: { label: 'Bulk Deleted', icon: '🗂️', color: 'text-red-600' },
    LOGIN: { label: 'Logged In', icon: '🔑', color: 'text-green-600' },
    LOGOUT: { label: 'Logged Out', icon: '🚪', color: 'text-gray-600' },
    VALIDATION_ERROR: { label: 'Validation Error', icon: '⚠️', color: 'text-yellow-600' }
  };
  
  const formatted = actionMap[action] || { 
    label: action, 
    icon: '📋', 
    color: 'text-gray-600' 
  };
  
  return {
    raw: action,
    ...formatted
  };
};

/**
 * Format resource type for display
 * @param {string} resource - Raw resource string
 * @returns {object} - Formatted resource info
 */
export const formatResource = (resource) => {
  const resourceMap = {
    sales: { label: 'Sale', icon: '💰', color: 'bg-green-100 text-green-800' },
    user: { label: 'User', icon: '👤', color: 'bg-blue-100 text-blue-800' },
    system: { label: 'System', icon: '⚙️', color: 'bg-gray-100 text-gray-800' },
    product: { label: 'Product', icon: '📦', color: 'bg-purple-100 text-purple-800' },
    client: { label: 'Client', icon: '🏢', color: 'bg-indigo-100 text-indigo-800' }
  };
  
  const formatted = resourceMap[resource] || { 
    label: resource, 
    icon: '📄', 
    color: 'bg-gray-100 text-gray-800' 
  };
  
  return {
    raw: resource,
    ...formatted
  };
};

/**
 * Generate a human-readable summary of the log entry
 * @param {object} logEntry - Raw log entry
 * @returns {string} - Summary text
 */
export const generateLogSummary = (logEntry) => {
  const action = formatAction(logEntry.action);
  const resource = formatResource(logEntry.resource);
  
  // Handle different types of log entries
  if (logEntry.resource === 'sales') {
    return generateSalesSummary(logEntry);
  }
  
  // Handle system logs
  if (logEntry.resource === 'system') {
    return generateSystemSummary(logEntry);
  }
  
  // Handle user logs
  if (logEntry.resource === 'user') {
    return generateUserSummary(logEntry);
  }
  
  if (logEntry.action.startsWith('BULK_')) {
    const itemCount = logEntry.metadata?.itemCount || 'multiple';
    return `${action.label} ${itemCount} ${resource.label.toLowerCase()}${itemCount !== 1 ? 's' : ''}`;
  }
  
  // Default format with better handling of undefined resourceId
  const resourceIdentifier = logEntry.resourceId ? ` #${logEntry.resourceId}` : 
                           logEntry.changes?.after?.transactionId ? ` #${logEntry.changes.after.transactionId}` :
                           logEntry.changes?.after?.clientName ? ` for ${logEntry.changes.after.clientName}` : '';
  
  return `${action.label} ${resource.label.toLowerCase()}${resourceIdentifier}`;
};

/**
 * Generate sales-specific summary
 * @param {object} logEntry - Sales log entry
 * @returns {string} - Sales summary
 */
export const generateSalesSummary = (logEntry) => {
  const action = formatAction(logEntry.action);
  const changes = logEntry.changes || {};
  
  if (logEntry.action === 'CREATE') {
    const clientName = changes.after?.clientName || 
                      changes.after?.clientEmail?.split('@')[0] || 
                      'Unknown Client';
    const price = changes.after?.price ? `$${changes.after.price}` : '';
    const transactionId = changes.after?.transactionId || logEntry.resourceId;
    
    return `${action.label} sale for ${clientName}${price ? ` - ${price}` : ''}${transactionId ? ` (${transactionId})` : ''}`;
  }
  
  if (logEntry.action === 'UPDATE') {
    const fieldsChanged = changes.fieldsChanged || [];
    const clientName = changes.after?.clientName || 
                      changes.before?.clientName || 
                      changes.after?.clientEmail?.split('@')[0] ||
                      changes.before?.clientEmail?.split('@')[0] ||
                      'Unknown Client';
    const transactionId = changes.after?.transactionId || 
                         changes.before?.transactionId || 
                         logEntry.resourceId;
    
    if (fieldsChanged.length === 0) {
      return `${action.label} sale for ${clientName}${transactionId ? ` (${transactionId})` : ''}`;
    }
    
    // Highlight important changes
    const importantChanges = [];
    
    if (fieldsChanged.includes('price')) {
      const priceChange = logEntry.metadata?.priceChange;
      if (priceChange) {
        importantChanges.push(`Price: $${priceChange.from} → $${priceChange.to}`);
      } else {
        importantChanges.push(`Price updated`);
      }
    }
    
    if (fieldsChanged.includes('status')) {
      const statusChange = logEntry.metadata?.statusChange;
      if (statusChange) {
        importantChanges.push(`Status: ${statusChange.from} → ${statusChange.to}`);
      } else {
        importantChanges.push(`Status updated`);
      }
    }
    
    if (fieldsChanged.includes('clientName')) {
      importantChanges.push(`Client name updated`);
    }
    
    if (fieldsChanged.includes('clientEmail')) {
      importantChanges.push(`Client email updated`);
    }
    
    if (importantChanges.length > 0) {
      return `${action.label} sale for ${clientName} - ${importantChanges.join(', ')}`;
    }
    
    const fieldsList = fieldsChanged.slice(0, 3).join(', ');
    const moreFields = fieldsChanged.length > 3 ? ` and ${fieldsChanged.length - 3} more` : '';
    
    return `${action.label} sale for ${clientName} - ${fieldsList}${moreFields}`;
  }
  
  if (logEntry.action === 'DELETE') {
    const clientName = changes.before?.clientName || 
                      changes.before?.clientEmail?.split('@')[0] || 
                      'Unknown Client';
    const transactionId = changes.before?.transactionId || logEntry.resourceId;
    
    return `${action.label} sale for ${clientName}${transactionId ? ` (${transactionId})` : ''}`;
  }
  
  return `${action.label} sale`;
};

/**
 * Generate system-specific summary
 * @param {object} logEntry - System log entry
 * @returns {string} - System summary
 */
export const generateSystemSummary = (logEntry) => {
  const action = formatAction(logEntry.action);
  
  if (logEntry.action === 'BACKUP') {
    return `${action.label} system data`;
  }
  
  if (logEntry.action === 'ERROR') {
    const errorType = logEntry.metadata?.errorType || 'system error';
    return `${action.label}: ${errorType}`;
  }
  
  if (logEntry.action === 'CLEANUP') {
    const operation = logEntry.metadata?.operation || 'system cleanup';
    return `${action.label}: ${operation}`;
  }
  
  return `${action.label} system operation`;
};

/**
 * Generate user-specific summary
 * @param {object} logEntry - User log entry
 * @returns {string} - User summary
 */
export const generateUserSummary = (logEntry) => {
  const action = formatAction(logEntry.action);
  const changes = logEntry.changes || {};
  
  if (logEntry.action === 'LOGIN') {
    const userEmail = logEntry.userEmail || 'Unknown user';
    return `${action.label} by ${userEmail}`;
  }
  
  if (logEntry.action === 'LOGOUT') {
    const userEmail = logEntry.userEmail || 'Unknown user';
    return `${action.label} by ${userEmail}`;
  }
  
  if (logEntry.action === 'CREATE') {
    const userEmail = changes.after?.email || 'Unknown user';
    return `${action.label} user account for ${userEmail}`;
  }
  
  if (logEntry.action === 'UPDATE') {
    const userEmail = changes.after?.email || changes.before?.email || 'Unknown user';
    const fieldsChanged = changes.fieldsChanged || [];
    
    if (fieldsChanged.length > 0) {
      return `${action.label} user ${userEmail} - ${fieldsChanged.join(', ')}`;
    }
    
    return `${action.label} user ${userEmail}`;
  }
  
  return `${action.label} user`;
};

/**
 * Format detailed log information
 * @param {object} logEntry - Raw log entry
 * @returns {object} - Formatted details
 */
export const formatLogDetails = (logEntry) => {
  const details = {
    changes: formatChanges(logEntry.changes),
    metadata: formatMetadata(logEntry.metadata),
    technical: {
      id: logEntry.id,
      resourceId: logEntry.resourceId,
      userId: logEntry.userId,
      severity: logEntry.severity,
      tags: logEntry.tags || []
    }
  };
  
  return details;
};

/**
 * Format changes object for display
 * @param {object} changes - Changes object from log entry
 * @returns {object} - Formatted changes
 */
export const formatChanges = (changes) => {
  if (!changes) return null;
  
  const formatted = {
    hasChanges: Boolean(changes.fieldsChanged && changes.fieldsChanged.length > 0),
    fieldsChanged: changes.fieldsChanged || [],
    fieldChanges: []
  };
  
  if (changes.fieldsChanged) {
    formatted.fieldChanges = changes.fieldsChanged.map(field => ({
      field,
      before: formatValueForDisplay(changes.before?.[field]),
      after: formatValueForDisplay(changes.after?.[field]),
      rawBefore: changes.before?.[field],
      rawAfter: changes.after?.[field]
    }));
  }
  
  return formatted;
};

/**
 * Format metadata for display
 * @param {object} metadata - Metadata object
 * @returns {object} - Formatted metadata
 */
export const formatMetadata = (metadata) => {
  if (!metadata) return null;
  
  return {
    source: metadata.source || 'web',
    userAgent: formatUserAgent(metadata.userAgent),
    sessionId: metadata.sessionId,
    priceChange: metadata.priceChange,
    statusChange: metadata.statusChange,
    clientInfo: metadata.clientInfo,
    bulkOperation: metadata.bulkOperation,
    itemCount: metadata.itemCount,
    validationErrors: metadata.validationErrors,
    errorCount: metadata.errorCount
  };
};

/**
 * Format user agent string for display
 * @param {string} userAgent - User agent string
 * @returns {object} - Formatted user agent info
 */
export const formatUserAgent = (userAgent) => {
  if (!userAgent) return null;
  
  // Simple user agent parsing
  const isChrome = userAgent.includes('Chrome');
  const isFirefox = userAgent.includes('Firefox');
  const isSafari = userAgent.includes('Safari') && !isChrome;
  const isEdge = userAgent.includes('Edge');
  
  let browser = 'Unknown';
  if (isChrome) browser = 'Chrome';
  else if (isFirefox) browser = 'Firefox';
  else if (isSafari) browser = 'Safari';
  else if (isEdge) browser = 'Edge';
  
  const isMobile = /Mobile|Android|iPhone|iPad/.test(userAgent);
  
  return {
    browser,
    isMobile,
    raw: userAgent
  };
};

/**
 * Get color class for severity level
 * @param {string} severity - Severity level
 * @returns {string} - CSS color class
 */
export const getSeverityColor = (severity) => {
  const colors = {
    LOW: 'text-green-600',
    MEDIUM: 'text-yellow-600',
    HIGH: 'text-orange-600',
    CRITICAL: 'text-red-600'
  };
  
  return colors[severity] || 'text-gray-600';
};

/**
 * Get icon for severity level
 * @param {string} severity - Severity level
 * @returns {string} - Icon emoji
 */
export const getSeverityIcon = (severity) => {
  const icons = {
    LOW: '🟢',
    MEDIUM: '🟡',
    HIGH: '🟠',
    CRITICAL: '🔴'
  };
  
  return icons[severity] || '⚪';
};

/**
 * Format logs for CSV export
 * @param {array} logs - Array of log entries
 * @returns {string} - CSV formatted string
 */
export const formatLogsForCSV = (logs) => {
  const headers = [
    'Timestamp',
    'User',
    'Action',
    'Resource',
    'Resource ID',
    'Summary',
    'Fields Changed',
    'Severity',
    'Source'
  ];
  
  const rows = logs.map(log => {
    const formatted = formatLogEntry(log);
    return [
      formatted.timestamp.full,
      formatted.user.email,
      formatted.action.label,
      formatted.resource.label,
      log.resourceId || '',
      formatted.summary,
      (log.changes?.fieldsChanged || []).join('; '),
      log.severity,
      log.metadata?.source || 'web'
    ];
  });
  
  const csvContent = [headers, ...rows]
    .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  
  return csvContent;
};

/**
 * Create a display-friendly change comparison
 * @param {object} before - Before value
 * @param {object} after - After value
 * @returns {string} - Formatted comparison
 */
export const formatChangeComparison = (before, after) => {
  const beforeDisplay = formatValueForDisplay(before);
  const afterDisplay = formatValueForDisplay(after);
  
  if (before === undefined) return `Added: ${afterDisplay}`;
  if (after === undefined) return `Removed: ${beforeDisplay}`;
  
  return `${beforeDisplay} → ${afterDisplay}`;
};
